import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "@iconify/react";
import { useDirection } from "../../../contexts/DirectionContext";
import { useTheme } from "../../../contexts/ThemeContext";
import Dropdown from "../Dropdown";

interface SearchParams {
  query: string;
  topics: string[];
  duration: string;
  format: string;
}

interface AdvancedSearchProps {
  className?: string;
  initialQuery?: string;
  onSearch: (params: SearchParams) => void;
  placeholder?: string;
}

const AdvancedSearch: React.FC<AdvancedSearchProps> = ({
  className = "",
  initialQuery = "",
  onSearch,
  placeholder,
}) => {
  const { t } = useTranslation();
  const { isRTL } = useDirection();
  const { theme } = useTheme();
  const [query, setQuery] = useState(initialQuery);
  const [searchType, setSearchType] = useState("video");

  const searchOptions = [
    { id: "video", label: t("search.videos"), icon: "ph:video-camera-thin" },
    { id: "shorts", label: t("search.shorts"), icon: "ph:play-circle-thin" },
    { id: "lives", label: t("search.lives"), icon: "ph:broadcast-thin" },
    { id: "topics", label: t("search.topics"), icon: "ph:book-open-thin" },
    { id: "audio", label: t("search.audio"), icon: "ph:microphone-thin" },
  ];

  const handleSearch = () => {
    onSearch({
      query: query.trim(),
      topics: [],
      duration: "",
      format: searchType,
    });
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  return (
    <div className={`${className} relative z-[1000]`}>
      <div
        // style={{
        //   border: "0.5px solid rgba(var(--colors-background-primary-rgb), 0.3)",
        // }}
        className={`
        relative
        flex items-stretch
        h-12 sm:h-14
        ${
          theme.mode === "dark"
            ? "bg-primary-900/30 text-primary-300 border-primary-700"
            : "bg-white/10 text-text-inverse border-text-inverse/30"
        }
        border
        rounded-full
        transition-all duration-200
        group/search
        overflow-visible
        ${className}
      `}
      >
        {/* Type Selector */}
        <div
          className={`
            relative
            flex-shrink-0
            flex items-stretch
            h-full
            px-3 sm:px-4
            ${isRTL ? 'rounded-r-full' : 'rounded-l-full'}
          `}
          style={{
            backgroundColor:
              theme.mode === "dark"
                ? "rgba(var(--colors-text-primary-rgb), 0.1)"
                : "rgba(var(--colors-background-primary-rgb), 0.1)",
            [isRTL ? "borderLeft" : "borderRight"]:
              theme.mode === "dark"
                ? "0.5px solid rgba(var(--colors-text-primary-rgb), 0.3)"
                : "0.5px solid rgba(var(--colors-background-primary-rgb), 0.3)",
          }}
        >
          <Dropdown
            trigger={(isOpen) => (
              <button
                type="button"
                aria-label={t("search.selectType")}
                className="
                  flex items-center gap-2
                  h-full
                  text-primary-500
                  hover:text-primary-600
                  focus:text-primary-600
                  focus:outline-none
                  focus:ring-0
                  focus:shadow-none
                  transition-colors
                  group
                  w-fit
                  p-0
                  bg-transparent
                  border-0
                  shadow-none
                  appearance-none
                "
              >
                <div className="flex items-center gap-2 h-full">
                  <Icon
                    icon={
                      searchOptions.find((opt) => opt.id === searchType)
                        ?.icon || "ph:video-camera-thin"
                    }
                    className={`
                      w-5 h-5
                      transition-transform
                      group-hover:scale-110
                      ${theme.mode === "dark" ? "text-primary-500" : "text-text-inverse"}
                    `}
                  />
                  <span 
                    className={`
                      hidden sm:inline text-sm font-medium whitespace-nowrap
                      ${theme.mode === "dark" ? "text-primary-500" : "text-text-inverse"}
                    `}
                  >
                    {searchOptions.find((opt) => opt.id === searchType)
                      ?.label || t("search.videos")}
                  </span>
                  <Icon
                    icon={isOpen ? "ph:caret-up-thin" : "ph:caret-down-thin"}
                    className={`
                      w-4 h-4 
                      opacity-60
                      transition-transform
                      group-hover:opacity-100
                      ${theme.mode === "dark" ? "text-primary-500" : "text-text-inverse"}
                    `}
                  />
                </div>
              </button>
            )}
            items={searchOptions.map((opt) => ({
              id: opt.id,
              label: opt.label,
              icon: opt.icon,
              onClick: () => setSearchType(opt.id),
              selected: opt.id === searchType,
            }))}
            width="auto"
            align={isRTL ? "right" : "left"}
            className="w-fit [&>div]:z-[1000] [&>div:last-child]:right-0"
          />
        </div>

        {/* Search Input */}
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder={placeholder || t("common.searchPlaceholder")}
          aria-label={t("common.search")}
          className={`
            flex-1
            px-3 sm:px-4
            bg-transparent 
            ${theme.mode === "dark" ? "text-text-primary" : "text-text-inverse"}
            placeholder-text-secondary/40
            text-sm sm:text-base
            font-light
            focus:outline-none
            transition-colors
            min-w-0
          `}
        />

        {/* Search Button */}
        <div className="flex items-center p-2">
          <button
            onClick={handleSearch}
            aria-label={t("common.search")}
            className={`
              flex-shrink-0
              flex items-center justify-center
              w-8 h-8 sm:w-10 sm:h-10
              bg-primary-500
              hover:bg-primary-600
              active:bg-primary-700
              focus:outline-none
              text-white
              transition-all duration-200
              group
              rounded-full
            `}
          >
            <Icon
              icon="ph:magnifying-glass-thin"
              className="
                w-4 h-4 sm:w-5 sm:h-5
                transition-transform
                group-hover:scale-110
                group-focus:scale-110
                text-white
              "
            />
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdvancedSearch;
